import { AssetType, ImageAnalyzeMode } from '@packages/global';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import ImageUpload from '../common/ImageUpload';

interface AssetGenerateSettingsProps {
  mode: 'text' | 'image';
  subjectType: AssetType;
  subjectName: string;
  editedDescription: string;
  editedPrompt: string;
  artStyle: string;
  uploadedImageUrls: string[];
  maxImageCount: number;
  isGenerating: boolean;
  balance: number;
  coinPrice: number;
  hasEnoughCoins: boolean;
  analyzingPrompt: boolean;
  onDescriptionChange: (v: string) => void;
  onPromptChange: (v: string) => void;
  onArtStyleChange: (v: string) => void;
  onImageUpload: (url: string) => void;
  onRemoveImage: (index: number) => void;
  onAnalyzePrompt: () => void;
}

export function AssetGenerateSettings({
  mode, subjectType, subjectName,
  editedDescription, editedPrompt, artStyle,
  uploadedImageUrls, maxImageCount,
  isGenerating, balance, coinPrice, hasEnoughCoins,
  analyzingPrompt,
  onDescriptionChange, onPromptChange, onArtStyleChange,
  onImageUpload, onRemoveImage, onAnalyzePrompt,
}: AssetGenerateSettingsProps) {
  const { t } = useTranslation(['projectSettings', 'common']);

  const getSubjectTypeLabel = () => {
    switch (subjectType) {
      case AssetType.CHARACTER: return t('asset.generateModalV2.subjectType.character');
      case AssetType.PROP: return t('asset.generateModalV2.subjectType.prop');
      case AssetType.SCENE: return t('asset.generateModalV2.subjectType.scene');
      default: return t('asset.generateModalV2.subjectType.default');
    }
  };

  const canAddMoreImage = uploadedImageUrls.length < maxImageCount;

  return (
    <div className="w-[280px] flex-shrink-0 flex flex-col gap-3">
      {/* 主体标签 */}
      <div className="flex items-center gap-2 h-[42px] flex-shrink-0">
        <span className="text-xs text-gray-400 bg-bg-form border border-border-subtle px-2 py-1 rounded flex-shrink-0">
          {getSubjectTypeLabel()}
        </span>
        <span className="text-sm text-gray-50 font-medium truncate">{subjectName}</span>
      </div>

      {/* 主体描述 */}
      <div className="flex flex-col flex-shrink-0">
        <label className="block text-xs text-gray-500 mb-1">
          {t('asset.generateSettings.subjectDescription')} <span className="text-gray-500">{t('asset.generateSettings.descriptionWriteBack')}</span>
        </label>
        <textarea
          value={editedDescription}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={3}
          disabled={isGenerating}
          placeholder={t('asset.generateSettings.descriptionPlaceholder')}
          className="w-full px-2.5 py-2 bg-bg-form border border-border-subtle rounded-lg text-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-xs disabled:opacity-50"
        />
      </div>

      {/* image 模式：参考图片 */}
      {mode === 'image' && (
        <div className="flex flex-col flex-shrink-0">
          <label className="block text-xs text-gray-500 mb-1">
            {t('asset.generateSettings.referenceImages')} <span className="text-red-500">*</span>
            <span className="text-gray-500 ml-1">({uploadedImageUrls.length}/{maxImageCount})</span>
          </label>
          <div className="grid grid-cols-3 gap-1.5">
            {uploadedImageUrls.map((url, index) => (
              <div key={index} className="relative group aspect-square">
                <img src={url} alt={t('asset.generateSettings.referenceImageAlt', { index: index + 1 })} className="w-full h-full object-cover rounded-lg" />
                <button
                  onClick={() => onRemoveImage(index)}
                  disabled={isGenerating}
                  className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500/80 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50 opacity-0 group-hover:opacity-100"
                >
                  <XMarkIcon className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
            {canAddMoreImage && (
              <div className="aspect-square flex items-center justify-center">
                <ImageUpload onUploadSuccess={onImageUpload} mode="default" className="w-full h-full" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* 特征提示词 */}
      <div className="flex flex-col flex-shrink-0">
        <label className="block text-xs text-gray-500 mb-1">
          {t('asset.generateSettings.featurePrompt')}
          {mode === 'image'
            ? <span className="text-red-500 ml-0.5">*</span>
            : <span className="text-gray-500 ml-1">{t('asset.generateSettings.promptWriteBack')}</span>
          }
        </label>
        <div className="relative">
          <textarea
            value={editedPrompt}
            onChange={(e) => onPromptChange(e.target.value)}
            rows={5}
            disabled={isGenerating}
            placeholder={mode === 'image'
              ? t('asset.generateSettings.promptPlaceholderImage')
              : t('asset.generateSettings.promptPlaceholderText')
            }
            className="w-full px-2.5 py-2 bg-bg-form border border-border-subtle rounded-lg text-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-xs disabled:opacity-50"
          />
          {/* image 模式下显示反推按钮 */}
          {mode === 'image' && (
            <button
              type="button"
              onClick={onAnalyzePrompt}
              disabled={isGenerating || analyzingPrompt || uploadedImageUrls.length === 0}
              className="absolute bottom-2 right-2 p-1 text-primary hover:text-primary/80 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
              title={uploadedImageUrls.length === 0 ? t('asset.generateSettings.uploadImageFirst') : t('asset.generateSettings.analyzePromptFromImage')}
            >
              <SparklesIcon className={`h-4 w-4 ${analyzingPrompt ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* 艺术风格 */}
      <div className="flex flex-col flex-shrink-0">
        <label className="block text-xs text-gray-500 mb-1">
          {t('asset.generateSettings.artStyle')} <span className="text-gray-500">{t('asset.generateSettings.artStyleOptional')}</span>
        </label>
        <textarea
          value={artStyle}
          onChange={(e) => onArtStyleChange(e.target.value)}
          rows={2}
          disabled={isGenerating}
          placeholder={t('asset.generateSettings.artStylePlaceholder')}
          className="w-full px-2.5 py-2 bg-bg-form border border-border-subtle rounded-lg text-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-xs disabled:opacity-50"
        />
      </div>

      {/* 剧点不足警告 */}
      {coinPrice > 0 && !hasEnoughCoins && (
        <div className="p-2 bg-red-500/10 rounded-lg border border-red-500/20 flex items-start gap-1.5 flex-shrink-0">
          <ExclamationTriangleIcon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-red-500">{t('asset.generateSettings.insufficientCoins', { balance, cost: coinPrice })}</span>
        </div>
      )}
    </div>
  );
}
